import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { unwrapResult } from "@reduxjs/toolkit";
import { useDispatch } from "react-redux";
import { loadStripe } from "@stripe/stripe-js";
import {
  createCustomerStripeSession,
  getMoveService,
  getUserByPhoneNumber,
  updateMoveService,
} from "../../redux/action/move-service";
import Header from "../header/Header";
import {
  confirmJob,
  createCustomer,
  createJob,
  createLogNotesInOpportunity,
  createOpportunity,
  createRooms,
  getBranches,
  getMoveSizes,
  getReferralSources,
  getTariffs,
  saveSmartMovingResponse,
} from "../../redux/action/smart-move-api";
const strikeKey = process.env.REACT_APP_STRIPE_KEY || "";
type SmartMovingIds = {
  customerId: string;
  opportunityId: string;
  rooms: any[]; // Replace 'any' with proper room type if available
  noteId: string;
  jobId: string;
};
const MoveSummary = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loadingMessage, setLoadingMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [moveData, setMoveData] = useState<any | null>(null);
  const [carrier, setCarrier] = useState<any>(null);
  const { canonicalId, businessName, carrierCanonicalId } = useParams();
  const [commission, setCommission] = useState(0.0);
  const lastStep = 11;
  const [inventory, setInventory] = useState<any>(null);
  const phoneNumber = localStorage.getItem("userPhoneNumber");
  const [user, setUser] = useState<any>(null);
  const [smartMovingOpportunityId, setSmartMovingOpportunityId] = useState("");
  const [smartMovingCustomerId, setSmartMovingCustomerId] = useState("");
  const [currentJobId, setCurrentJobId] = useState("");

  const [noteId, setNoteId] = useState<any>("");
  const [smartMovingRooms, setSmartMovingRooms] = useState<any[]>([]);

  const calculateInitialDeposit = (carrier: any, move: any) => {
    if (carrier.rate.deposit_amount) {
      setCommission(Number(carrier.rate.deposit_amount));
    } else if (carrier.rate.deposit_percentage) {
      const percentageValue = carrier.rate.deposit_percentage / 100;
      setCommission(Math.ceil(move.price_of_items_only * percentageValue));
    }
  };
  useEffect(() => {
    if (canonicalId) {
      const moveServiceData = {
        canonical_id: canonicalId,
        last_step: lastStep,
      };
      try {
        dispatch<any>(getMoveService(canonicalId))
          .then(unwrapResult)
          .then((res: any) => {
            setInventory(res.inventory?.inventory);
            setMoveData(res);
            setCarrier(res.carrier);
            calculateInitialDeposit(res.carrier, res);
            const updateMove = dispatch<any>(
              updateMoveService(moveServiceData)
            );
            unwrapResult(updateMove);
          })
          .catch((err: any) => {
            console.error("Failed to fetch move data:", err);
          });
      } catch (error) {
        console.log(error);
      }
    }
  }, [canonicalId, dispatch]);
  const [bulkyData, setBulkyData] = useState<any>(null);

  useEffect(() => {
    if (inventory) {
      try {
        const inventoryData = JSON.parse(inventory);
        const bulkyItems: any[] = [];

        inventoryData.forEach((room: any) => {
          room.images.forEach((image: any) => {
            image.items.forEach((item: any) => {
              if (item.bulky) {
                bulkyItems.push({
                  object_name: item.object_name,
                  price: item.price,
                });
              }
            });
          });
        });

        setBulkyData(bulkyItems.length > 0 ? bulkyItems : null);
      } catch (error) {
        console.error("Error parsing inventory:", error);
        setBulkyData(null);
      }
    }
  }, [inventory]); // Add inventory as dependency

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const parseItems = (itemsString: string) => {
    try {
      return JSON.parse(itemsString);
    } catch (e) {
      return [];
    }
  };
  const showBulkyItemList = (itemList: string) => {
    const items = JSON.parse(itemList);
    const array = Object.keys(items).map((key) => ({
      name: key,
      price: items[key],
    }));
    return array;
  };
  useEffect(() => {
    if (phoneNumber) {
      dispatch<any>(getUserByPhoneNumber(phoneNumber))
        .then(unwrapResult)
        .then((res: any) => {
          // const currentUserId = res.id
          setUser(res);
        })
        .catch((error: any) => {
          console.error("Error fetching user by phone number:", error);
        });
    }
  }, []);
  const formatInventoryForNotes = (inventory: any): string => {
    try {
      let formattedText = "";

      // Parse the inventory if it's a string
      const inventoryData =
        typeof inventory === "string" ? JSON.parse(inventory) : inventory;

      // Check if inventoryData is an array before using forEach
      if (!Array.isArray(inventoryData)) {
        console.error("Inventory is not a valid array:", inventoryData);
        return "No inventory data available";
      }

      inventoryData?.forEach((room: any, roomIndex: number) => {
        formattedText += `Room ${roomIndex + 1}: ${
          room.roomName || "Unnamed Room"
        }\n`;

        room?.images?.forEach((image: any, imageIndex: number) => {
          formattedText += `Image ${imageIndex + 1}:\n`;
          formattedText += `URL: ${image.imageUrl || "No URL available"}\n`;

          image?.items?.forEach((item: any, itemIndex: number) => {
            // Replace "x" with "-" for the count
            formattedText += `${itemIndex + 1}. ${item.count || 1}- ${
              item.object_name || "Unknown Item"
            }\n`;

            if (
              item.predicted_dimensions &&
              Array.isArray(item.predicted_dimensions)
            ) {
              formattedText += `   Dimensions: ${item.predicted_dimensions.join(
                " x "
              )} ft\n`;
            }

            if (item.bulky !== undefined) {
              formattedText += `   Bulky: ${item.bulky ? "Yes" : "No"}\n`;
            }

            if (item.price !== null && item.price !== undefined) {
              formattedText += `   Price: $${item.price}\n`;
            }

            formattedText += "\n";
          });
        });

        formattedText += "\n";
      });

      return formattedText;
    } catch (error) {
      console.error("Error formatting inventory:", error);
      return "Error formatting inventory data";
    }
  };
  useEffect(() => {
    const triggerSmartMovingApis = async () => {
      let customerId: string = "";
      let opportunityId: string = "";
      let rooms: any[] = []; // Replace 'any' with proper room type if available
      let noteId: string = "";
      let jobId: string = "";

      if (carrier?.smart_moving_key && user) {
        try {
          const customerData = {
            carrierId: carrier?.id,
            createCustomer: {
              name: `${user?.first_name} ${user?.last_name}`,
              phoneNumber,
              phoneType: 0,
              emailAddress: user?.email,
              address: "",
              secondaryPhoneNumbers: [],
            },
          };
          setIsLoading(true);
          // Create Customer
          setLoadingMessage("Fetching configuration...");

          const [
            referralSourcesRes,
            branchesRes,
            tariffsRes,
            moveSizesRes,
            // salesPersonsRes
          ] = await Promise.all([
            dispatch<any>(getReferralSources(carrier.id)).then(unwrapResult),
            dispatch<any>(getBranches(carrier.id)).then(unwrapResult),
            dispatch<any>(getTariffs(carrier.id)).then(unwrapResult),
            dispatch<any>(getMoveSizes(carrier.id)).then(unwrapResult),
          ]);

          const referralSourceId = referralSourcesRes.pageResults[2]?.id;
          const branchId = branchesRes.pageResults[0]?.id;
          const tariffId = tariffsRes.pageResults[0]?.id;
          const moveSizeId = moveSizesRes.pageResults[0]?.id;

          if (!referralSourceId || !branchId || !tariffId || !moveSizeId) {
            throw new Error("Failed to fetch required configuration data");
          }
          setLoadingMessage("Creating customer...");
          dispatch<any>(createCustomer(customerData))
            .then(unwrapResult)
            .then((res: any) => {
              setLoadingMessage("Creating opportunity...");

              customerId = res.id || res;
              setSmartMovingCustomerId(customerId);
              const from_parts = moveData.moving_from.split(",");
              const from_street = from_parts[0] ? from_parts[0].trim() : "";
              const from_city = from_parts[1] ? from_parts[1].trim() : "";
              const from_state = from_parts[2] ? from_parts[2].trim() : "";
              const from_zip = from_parts[3] ? from_parts[3].trim() : "";
              const from_country = from_parts[4] ? from_parts[4].trim() : "";

              const to_parts = moveData.moving_from.split(",");
              const to_street = to_parts[0] ? to_parts[0].trim() : "";
              const to_city = to_parts[1] ? to_parts[1].trim() : "";
              const to_state = to_parts[2] ? to_parts[2].trim() : "";
              const to_zip = to_parts[3] ? to_parts[3].trim() : "";
              const to_country = to_parts[4] ? to_parts[4].trim() : "";

              // Create Opportunity
              const opportunityPayload = {
                carrierId: carrier?.id,
                createOpportunity: {
                  customerId: customerId,
                  referralSourceId: referralSourceId,
                  tariffId: tariffId,
                  branchId: branchId,
                  moveDate: moveData.moving_date.split("T")[0],
                  moveSizeId: moveSizeId,
                  salesPersonId: "a42c328c-740f-420c-26fb-08dba0017b09",
                  serviceTypeId: 1,
                  utmInformation: {
                    utmAdGroup: "",
                    utmCampaign: "",
                    utmContent: "",
                    utmCustomTracking: "",
                    utmKeyword: "",
                    utmMedium: "",
                    utmSource: "",
                  },
                  originAddress: {
                    fullAddress: moveData?.moving_from,
                    street: from_street,
                    unit: "",
                    city: from_city,
                    state: from_state,
                    zip: from_zip,
                    lat: "",
                    lng: "",
                    country: from_country,
                  },
                  destinationAddress: {
                    fullAddress: moveData?.moving_to,
                    street: to_street,
                    unit: "string",
                    city: to_city,
                    state: to_state,
                    zip: to_zip,
                    lat: "",
                    lng: "",
                    country: to_country,
                  },
                },
              };
              dispatch<any>(createOpportunity(opportunityPayload))
                .then(unwrapResult)
                .then((res: any) => {
                  opportunityId = res.opportunityId || res;
                  setSmartMovingOpportunityId(opportunityId);

                  if (inventory != null && inventory !== "") {
                    setLoadingMessage("Creating rooms...");
                    const roomTypes = [
                      {
                        id: "1224b03c-e462-4e04-8fa1-b0f901473af6",
                        name: "Bedroom",
                      },
                      {
                        id: "bc55c016-eb41-44fe-8f8a-b0f901473af6",
                        name: "Dining Room",
                      },
                      {
                        id: "abe2a918-2dcc-4d08-8efc-b0f901473af6",
                        name: "Kids Room",
                      },
                      {
                        id: "9d575d5a-6355-4327-ad19-b0f901473af6",
                        name: "Kitchen",
                      },
                      {
                        id: "c7e7b2de-8a58-49d0-93db-b0f901473af6",
                        name: "Living Room",
                      },
                      {
                        id: "5dd1aaee-a415-41ae-aade-b0f901473af6",
                        name: "Office",
                      },
                      {
                        id: "f3165fd2-08fd-4890-aa15-b0f901473af6",
                        name: "Other",
                      },
                      {
                        id: "82f6d559-8bb2-443d-868c-b0f901473af6",
                        name: "Outdoor",
                      },
                    ];
                    // 1. Parse inventory
                    let roomsArray = [];
                    try {
                      roomsArray = Array.isArray(inventory)
                        ? inventory
                        : JSON.parse(inventory);
                      if (!Array.isArray(roomsArray))
                        throw new Error("Inventory is not an array");
                    } catch (e) {
                      console.error("Failed to parse inventory:", e);
                      return;
                    }

                    // 2. Create room type lookup (case-insensitive)
                    const roomTypeMap = new Map();
                    roomTypes.forEach((room) => {
                      roomTypeMap.set(room.name.toLowerCase(), room);
                    });

                    const roomsPayload: any[] = [];
                    const seenRooms = new Set();

                    roomsArray.forEach((inventoryRoom) => {
                      const roomName =
                        inventoryRoom.roomName?.toLowerCase() || "other";
                      const matchedRoomType = roomTypeMap.get(roomName) || {
                        id: "f3165fd2-08fd-4890-aa15-b0f901473af6", // Other
                        name: "Other",
                      };

                      if (!seenRooms.has(roomName)) {
                        seenRooms.add(roomName);
                        roomsPayload.push({
                          name: inventoryRoom.roomName, // Original room name with correct casing
                          roomTypeId: matchedRoomType.id,
                        });
                      }
                    });
                    try {
                      dispatch<any>(
                        createRooms({
                          carrierId: carrier.id,
                          opportunityId,
                          rooms: roomsPayload,
                        })
                      )
                        .then(unwrapResult)
                        .then((res: any) => {
                          rooms = res;
                          setSmartMovingRooms(rooms);
                        });
                    } catch (error) {
                      // console.error("Failed to create rooms:", error);
                      return false;
                    }

                    const formattedInventory =
                      formatInventoryForNotes(inventory);
                    const notesData = {
                      carrierId: carrier.id,
                      opportunityId,
                      notes: formattedInventory,
                    };
                    dispatch<any>(createLogNotesInOpportunity(notesData))
                      .then(unwrapResult)
                      .then((res: any) => {
                        noteId = res.noteId;
                        setNoteId(noteId);
                      });
                  }
                  // Create Job
                  setLoadingMessage("Creating job...");
                  const jobData = {
                    carrierId: carrier?.id,
                    createService: {
                      serviceType: 1,
                    },
                  };

                  dispatch<any>(
                    createJob({
                      opportunityId,
                      jobData,
                    })
                  )
                    .then(unwrapResult)
                    .then((res: any) => {
                      jobId = res.jobId || res;
                      setCurrentJobId(jobId);
                    });
                  setIsLoading(false);
                });
            })
            .catch((error: any) => {
              // console.error("Error to summit smart moving apis", error);
              return false;
            });
        } catch (error) {
          // console.error("Error in SmartMoving APIs:", error);
          return false;
        }
      }
    };

    triggerSmartMovingApis();
  }, [carrier?.smart_moving_key]);
  const [hasSaved, setHasSaved] = useState(false);

  useEffect(() => {
    const saveData = async () => {
      if (
        !hasSaved &&
        smartMovingCustomerId &&
        smartMovingOpportunityId &&
        currentJobId
      ) {
        try {
          setLoadingMessage("Saving response...");
          setIsLoading(true);

          const smartMovingData = {
            customer_id: smartMovingCustomerId,
            opportunity_id: smartMovingOpportunityId,
            job_id: currentJobId,
            rooms: smartMovingRooms,
            notes_id: noteId,
            userId: user?.id,
          };

          dispatch<any>(saveSmartMovingResponse(smartMovingData))
            .then(unwrapResult)
            .then((res: any) => {
              console.log(res, "Data saved successfully");
            });
          setHasSaved(true);
        } catch (error) {
          return false;
        } finally {
          setIsLoading(false);
        }
      }
    };

    saveData();
  }, [smartMovingCustomerId, smartMovingOpportunityId, currentJobId, hasSaved]);
  const smartMoving = async () => {
    if (carrier?.smart_moving_key) {
      try {
        // Create Job

        setLoadingMessage("Confirming job...");
        const confirmData = {
          opportunityId: smartMovingOpportunityId,
          currentJobId,
          carrierId: Number(carrier?.id), // Ensure this is a number
        };

        dispatch<any>(confirmJob(confirmData))
          .then(unwrapResult)
          .then((res: any) => {
            console.log(res);
          });
        return true;
      } catch (error) {
        return false;
      }
    }
    return false;
  };
  const createCheckoutSession = async () => {
    setIsLoading(true);
    setLoadingMessage("Creating Checkout Session...");
    await smartMoving();
    const stripe = await loadStripe(strikeKey);
    const checkoutParams = {
      price: commission * 100,
      carrierId: carrier.id,
      canonical_id: canonicalId,
    };
    dispatch<any>(createCustomerStripeSession(checkoutParams))
      .then(unwrapResult)
      .then((responseSession: any) => {
        stripe?.redirectToCheckout({
          sessionId: responseSession.sessionId,
        });
        setTimeout(() => {
          setIsLoading(false);
        }, 1300);
      })
      .catch((err: any) => {
        console.log(err);
        setTimeout(() => {
          setIsLoading(false);
        }, 1300);
      });
  };
  return (
    <div>
      <Header />
      <Container
        sx={{
          width: "100%",
          maxWidth: "100%",
          minHeight: isMobile ? "calc(100vh - 64px)" : "80vh",
          boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
          padding: isMobile ? "16px 12px" : "20px",
          background: "#f2f0f0",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            paddingBottom: isMobile ? "80px" : "20px",
          }}
        >
          <Typography
            fontWeight={700}
            fontSize={isMobile ? "18px" : "20px"}
            textAlign="center"
            mb={isMobile ? 1.5 : 2}
          >
            Move Summary
          </Typography>
          <Divider sx={{ background: "#D3D3D3", mb: isMobile ? 1.5 : 2 }} />

          {moveData && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: isMobile ? "flex-start" : "center",
                  flexDirection: isMobile ? "column" : "row",
                  mb: isMobile ? 1.5 : 2,
                  gap: isMobile ? 0.5 : 0,
                }}
              >
                <Typography fontWeight={600} fontSize={isMobile ? 14 : 16}>
                  Move Number #:
                </Typography>
                <Typography
                  fontWeight={400}
                  fontSize={isMobile ? 14 : 16}
                  textAlign={isMobile ? "left" : "right"}
                  sx={{ wordBreak: "break-word" }}
                >
                  {moveData?.move_order_number || "Not provided"}{" "}
                </Typography>
              </Box>
              <Divider sx={{ background: "#D3D3D3", mb: isMobile ? 1.5 : 2 }} />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: isMobile ? "flex-start" : "center",
                  flexDirection: isMobile ? "column" : "row",
                  mb: isMobile ? 1.5 : 2,
                  gap: isMobile ? 0.5 : 0,
                }}
              >
                <Typography fontWeight={600} fontSize={isMobile ? 14 : 16}>
                  Name:
                </Typography>
                <Typography
                  fontWeight={400}
                  fontSize={isMobile ? 14 : 16}
                  textAlign={isMobile ? "left" : "right"}
                  sx={{ wordBreak: "break-word" }}
                >
                  {moveData?.user?.first_name || "Not provided"}{" "}
                  {moveData?.user?.last_name || ""}
                </Typography>
              </Box>
              <Divider sx={{ background: "#D3D3D3", mb: isMobile ? 1.5 : 2 }} />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: isMobile ? "flex-start" : "center",
                  flexDirection: isMobile ? "column" : "row",
                  mb: isMobile ? 1.5 : 2,
                  gap: isMobile ? 0.5 : 0,
                }}
              >
                <Typography fontWeight={600} fontSize={isMobile ? 14 : 16}>
                  Phone Number:
                </Typography>
                <Typography
                  fontWeight={400}
                  fontSize={isMobile ? 14 : 16}
                  textAlign={isMobile ? "left" : "right"}
                  sx={{ wordBreak: "break-word" }}
                >
                  {moveData?.user?.phone_number
                    ? "+1" + moveData?.user?.phone_number
                    : "Not provided"}
                </Typography>
              </Box>
              <Divider sx={{ background: "#D3D3D3" }} />
              <Box sx={{ mb: isMobile ? 2 : 3 }}>
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: isMobile ? "flex-start" : "center",
                      justifyContent: "space-between",
                      flexDirection: isMobile ? "column" : "row",
                      cursor: "pointer",
                      padding: isMobile ? "8px 0" : "12px 0",
                      borderRadius: "12px",
                      gap: isMobile ? 0.5 : 0,
                    }}
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  >
                    <Typography fontWeight={600} fontSize={isMobile ? 14 : 16}>
                      Move Type:
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography
                        fontWeight={400}
                        fontSize={isMobile ? 14 : 16}
                        sx={{ mr: 1 }}
                        textAlign={isMobile ? "left" : "right"}
                      >
                        {Number(moveData.distance) > 200
                          ? "Long Distance"
                          : "Local"}
                      </Typography>
                    </Box>
                  </Box>
                  <Divider
                    sx={{ background: "#D3D3D3", mb: isMobile ? 1.5 : 2 }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: isMobile ? "flex-start" : "center",
                      flexDirection: isMobile ? "column" : "row",
                      mb: isMobile ? 1.5 : 2,
                      gap: isMobile ? 0.5 : 0,
                    }}
                  >
                    <Typography fontWeight={600} fontSize={isMobile ? 14 : 16}>
                      Number of Rooms/Bedrooms:
                    </Typography>
                    <Typography
                      fontWeight={400}
                      fontSize={isMobile ? 14 : 16}
                      textAlign={isMobile ? "left" : "right"}
                    >
                      {moveData.number_of_bedrooms}
                    </Typography>
                  </Box>
                  <Divider
                    sx={{ background: "#D3D3D3", mb: isMobile ? 1.5 : 2 }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: isMobile ? "flex-start" : "center",
                      flexDirection: isMobile ? "column" : "row",
                      mb: isMobile ? 1.5 : 2,
                      gap: isMobile ? 0.5 : 0,
                    }}
                  >
                    <Typography fontWeight={600} fontSize={isMobile ? 14 : 16}>
                      Distance:
                    </Typography>
                    <Typography
                      fontWeight={400}
                      fontSize={isMobile ? 14 : 16}
                      textAlign={isMobile ? "left" : "right"}
                    >
                      {moveData.distance}
                    </Typography>
                  </Box>
                  <Divider
                    sx={{ background: "#D3D3D3", mb: isMobile ? 1.5 : 2 }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: isMobile ? "flex-start" : "center",
                      flexDirection: isMobile ? "column" : "row",
                      mb: isMobile ? 1.5 : 2,
                      gap: isMobile ? 0.5 : 0,
                    }}
                  >
                    <Typography fontWeight={600} fontSize={isMobile ? 14 : 16}>
                      Travel Time:
                    </Typography>
                    <Typography
                      fontWeight={400}
                      fontSize={isMobile ? 14 : 16}
                      textAlign={isMobile ? "left" : "right"}
                    >
                      {moveData.duration_time}
                    </Typography>
                  </Box>
                  <Divider
                    sx={{ background: "#D3D3D3", mb: isMobile ? 1.5 : 2 }}
                  />
                  <Box
                    sx={{
                      backgroundColor: "#f9f9f9",
                      borderRadius: "12px",
                      padding: isMobile ? "10px" : "12px",
                      textAlign: "left",
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <Typography fontSize={isMobile ? 14 : 16} sx={{ mb: 1 }}>
                      <strong>Bulky Items:</strong>
                    </Typography>
                    <Box sx={{ pl: isMobile ? 1 : 0 }}>
                      {Number(moveData.distance) > 200 ? (
                        bulkyData ? (
                          bulkyData.map((item: any, index: any) => (
                            <Typography
                              key={index}
                              fontSize={isMobile ? 13 : 14}
                              sx={{ mb: 0.5, wordBreak: "break-word" }}
                            >
                              {item.object_name} - ${item.price}
                            </Typography>
                          ))
                        ) : (
                          <Typography fontSize={isMobile ? 13 : 14}>
                            No Bulky Items
                          </Typography>
                        )
                      ) : moveData.items ? (
                        JSON.parse(moveData.items).map(
                          (item: any, index: any) => (
                            <Typography
                              key={index}
                              fontSize={isMobile ? 13 : 14}
                              sx={{ mb: 0.5, wordBreak: "break-word" }}
                            >
                              {item.name} - ${item.fee}
                            </Typography>
                          )
                        )
                      ) : (
                        <Typography fontSize={isMobile ? 13 : 14}>
                          No Bulky Items
                        </Typography>
                      )}
                    </Box>
                    {/* <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 2,
                        }}
                      >
                        <Typography fontSize={16}>
                          <strong>Floor Type:</strong>
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Typography fontSize={16}>
                            {moveData.floor_type}
                          </Typography>
                        </Box>
                      </Box> */}
                    {/* <Typography fontSize={16}>
                        <strong>Floor Number:</strong> {moveData.floor_number}
                      </Typography> */}
                    {/* <Typography fontSize={16}>
                        <strong>Additional Floor:</strong>{" "}
                        {moveData.additional_floor}
                      </Typography> */}
                  </Box>
                </Box>
              </Box>
              <Divider sx={{ background: "#D3D3D3", mb: isMobile ? 1.5 : 2 }} />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: isMobile ? "flex-start" : "center",
                  flexDirection: isMobile ? "column" : "row",
                  mb: isMobile ? 1.5 : 2,
                  gap: isMobile ? 0.5 : 0,
                }}
              >
                <Typography fontWeight={600} fontSize={isMobile ? 14 : 16}>
                  Date of Move:
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    fontWeight={400}
                    fontSize={isMobile ? 14 : 16}
                    sx={{ mr: 1 }}
                    textAlign={isMobile ? "left" : "right"}
                  >
                    {formatDate(moveData.moving_date)}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ background: "#D3D3D3", mb: isMobile ? 1.5 : 2 }} />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: isMobile ? "flex-start" : "center",
                  flexDirection: isMobile ? "column" : "row",
                  mb: isMobile ? 1.5 : 2,
                  gap: isMobile ? 0.5 : 0,
                }}
              >
                <Typography fontWeight={600} fontSize={isMobile ? 14 : 16}>
                  Pickup Location:
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    maxWidth: isMobile ? "100%" : "60%",
                  }}
                >
                  <Typography
                    fontWeight={400}
                    fontSize={isMobile ? 14 : 16}
                    sx={{
                      mr: 1,
                      wordBreak: "break-word",
                      textAlign: isMobile ? "left" : "right",
                    }}
                  >
                    {moveData.moving_from}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ background: "#D3D3D3", mb: isMobile ? 1.5 : 2 }} />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: isMobile ? "flex-start" : "center",
                  flexDirection: isMobile ? "column" : "row",
                  mb: isMobile ? 1.5 : 2,
                  gap: isMobile ? 0.5 : 0,
                }}
              >
                <Typography fontWeight={600} fontSize={isMobile ? 14 : 16}>
                  Delivery Location:
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    maxWidth: isMobile ? "100%" : "60%",
                  }}
                >
                  <Typography
                    fontWeight={400}
                    fontSize={isMobile ? 14 : 16}
                    sx={{
                      mr: 1,
                      wordBreak: "break-word",
                      textAlign: isMobile ? "left" : "right",
                    }}
                  >
                    {moveData.moving_to}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ background: "#D3D3D3", mb: isMobile ? 1.5 : 2 }} />

              <Box sx={{ mb: isMobile ? 2 : 3 }}>
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: isMobile ? "flex-start" : "center",
                      justifyContent: "space-between",
                      flexDirection: isMobile ? "column" : "row",
                      cursor: "pointer",
                      padding: isMobile ? "8px 0" : "12px 0",
                      borderRadius: "12px",
                      gap: isMobile ? 0.5 : 0,
                    }}
                  >
                    <Typography fontWeight={600} fontSize={isMobile ? 14 : 16}>
                      Packing:
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography
                        fontWeight={400}
                        fontSize={isMobile ? 14 : 16}
                        sx={{ mr: 1 }}
                        textAlign={isMobile ? "left" : "right"}
                      >
                        {moveData.packingPrice ? `YES` : "NO"}
                      </Typography>
                    </Box>
                  </Box>
                  <Divider
                    sx={{ background: "#D3D3D3", mb: isMobile ? 1.5 : 2 }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: isMobile ? "flex-start" : "center",
                      flexDirection: isMobile ? "column" : "row",
                      mb: isMobile ? 1.5 : 2,
                      gap: isMobile ? 0.5 : 0,
                    }}
                  >
                    <Typography fontWeight={600} fontSize={isMobile ? 14 : 16}>
                      Additional Insurance:
                    </Typography>
                    <Typography
                      color={theme.palette.primary.main}
                      fontWeight={700}
                      fontSize={isMobile ? 16 : 18}
                      textAlign={isMobile ? "left" : "right"}
                    >
                      {moveData.isInsurance ? "YES" : "NO"}
                    </Typography>
                  </Box>
                </Box>

                <Divider
                  sx={{ background: "#D3D3D3", mb: isMobile ? 1.5 : 2 }}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: isMobile ? "flex-start" : "center",
                    flexDirection: isMobile ? "column" : "row",
                    mb: isMobile ? 1.5 : 2,
                    gap: isMobile ? 0.5 : 0,
                  }}
                >
                  <Typography fontWeight={600} fontSize={isMobile ? 14 : 16}>
                    Total Move Cost:
                  </Typography>
                  <Typography
                    fontWeight={400}
                    fontSize={isMobile ? 16 : 16}
                    textAlign={isMobile ? "left" : "right"}
                  >
                    {moveData.packingPrice
                      ? `$${Math.ceil(
                          moveData.packingPrice + Number(moveData.price)
                        )}`
                      : `$${Math.ceil(moveData.price)}`}
                  </Typography>
                </Box>
                <Divider sx={{ background: "#D3D3D3" }} />
              </Box>
              {/* Total Price Section */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: isMobile ? "flex-start" : "center",
                  flexDirection: isMobile ? "column" : "row",
                  mb: isMobile ? 1.5 : 2,
                  gap: isMobile ? 0.5 : 0,
                }}
              >
                <Typography
                  color="success"
                  fontWeight={600}
                  fontSize={isMobile ? 14 : 16}
                >
                  Cost to Book Move:
                </Typography>
                <Typography
                  color="success"
                  fontWeight={700}
                  fontSize={isMobile ? 16 : 16}
                  textAlign={isMobile ? "left" : "right"}
                >
                  {/* {moveData.price.split(":")[1]?.trim() || "Not specified"} */}
                  ${commission}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        {isLoading && (
          <Backdrop
            sx={{
              color: `${theme.palette.primary}`,
              zIndex: (theme) => theme.zIndex.drawer + 1,
              backdropFilter: "blur(5px)",
              backgroundColor: "rgba(0,0,0,0.2)",
            }}
            open={isLoading}
          >
            <CircularProgress size={50} color="inherit" />
            <Typography
              variant="h6"
              sx={{ mt: 2, color: `${theme.palette.primary}`, marginLeft: 1 }}
            >
              {loadingMessage}
            </Typography>
          </Backdrop>
        )}

        {moveData ? (
          <Box
            sx={{
              width: "100%",
              paddingBottom: isMobile ? "0" : "20px",
              position: isMobile ? "fixed" : "relative",
              bottom: isMobile ? 0 : "auto",
              left: isMobile ? 0 : "auto",
              right: isMobile ? 0 : "auto",
              padding: isMobile ? "12px" : "0",
              backgroundColor: isMobile ? "#f2f0f0" : "transparent",
              borderTop: isMobile ? "1px solid #e0e0e0" : "none",
              zIndex: isMobile ? 1000 : "auto",
              boxSizing: "border-box",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Button
              variant="contained"
              sx={{
                width: isMobile ? "calc(100% - 0px)" : "100%",
                maxWidth: isMobile ? "none" : "100%",
                borderRadius: "20px",
                backgroundColor: theme.palette.primary.main,
                color: "#fff",
                padding: "12px 24px",
                textTransform: "none",
                fontSize: "14px",
                fontWeight: "600",
                "&:hover": { backgroundColor: "#357ABD" },
                margin: 0,
                boxSizing: "border-box",
              }}
              onClick={() => {
                if (
                  moveData.initial_deposit === null &&
                  !moveData.payment_response
                ) {
                  createCheckoutSession();
                } else {
                  navigate(
                    `/${businessName}/${carrierCanonicalId}/consumer-dashboard/${moveData.user.canonical_id}`
                  );
                }
              }}
            >
              {moveData.initial_deposit === null && !moveData.payment_response
                ? "Confirm Move"
                : "Go To Dashboard"}
            </Button>
          </Box>
        ) : null}
      </Container>
    </div>
  );
};

export default MoveSummary;
